import React from "react";

import { Form } from "~/components/form";
import { Tabs } from "~/components/tabs";
import { TabsContent } from "~/components/tabs/content";
import { TabsList } from "~/components/tabs/list";
import { TabsTrigger } from "~/components/tabs/trigger";
import { cx } from "~/tools/class-variance";
import { useScoped } from "~/tools/impulse";
import { usePropagateFocusWhenInvalid } from "~/tools/impulse-form/use-propagate-focus-when-invalid";
import { useIsSticking } from "~/tools/use-is-sticking";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { DashboardWidgetSpec } from "../../common/dashboard-spec";
import { DashboardSaveAlert } from "../../routes/dashboards/dashboard/save-alert";
import { useClosestScrollingContainer } from "../_scrolling-container";

import { MarkdownSettings } from "./_settings/markdown";
import { SetupDashboardWidgetYaml } from "./_yaml/field";
import { FormState } from "./_form-state";
import { SetupDashboardWidgetGenericFields } from "./_generic-fields";
import { SetupDashboardWidgetLinks } from "./_links-fields";
import { SetupDashboardWidgetPreview } from "./_preview";
import { SetupDashboardWidgetQueries } from "./_queries-fields";
import { Settings } from "./_settings";

const TAB_QUERIES = "queries";
const TAB_SETTINGS = "settings";
const TAB_LINKS = "links";
const TAB_YAML = "yaml";

export interface SetupDashboardWidgetFormProps {
	formId: string;
	timeRange: TimeRange;
	form: FormState;
	/**
	 * Passes `undefined` when the form is not changed.
	 */
	onSave: (value: undefined | DashboardWidgetSpec) => void;
}

export const SetupDashboardWidgetForm: React.FC<SetupDashboardWidgetFormProps> = ({
	formId,
	timeRange,
	form,
	onSave,
}) => {
	const isMarkdownKind = useScoped((scope) => form.fields.kind.active.getInput(scope) === "markdown", [form]);

	const [activeTab, setActiveTab] = React.useState(TAB_QUERIES);

	const tabsListRef = React.useRef<HTMLDivElement>(null);
	const { element: scrollingContainer } = useClosestScrollingContainer();
	const isSticky = useIsSticking(tabsListRef.current, { scrollingContainer });

	usePropagateFocusWhenInvalid(isMarkdownKind ? undefined : form.fields.kind.branches.metrics.fields.queries, () =>
		setActiveTab(TAB_QUERIES),
	);
	usePropagateFocusWhenInvalid(isMarkdownKind ? undefined : form.fields.kind.branches.metrics.fields.settings, () =>
		setActiveTab(TAB_SETTINGS),
	);
	usePropagateFocusWhenInvalid(form.fields.links, () => setActiveTab(TAB_LINKS));

	return (
		<Form
			className="flex h-full flex-col"
			id={formId}
			onSubmit={(output, innerScope) => {
				if (form.isDirty(innerScope)) {
					onSave(FormState.outputToSpec(output));
				} else {
					onSave(undefined);
				}
			}}
			state={form}
		>
			<DashboardSaveAlert className="mb-4" placement="dashboard-widget-drawer" />

			<div className="flex gap-6">
				<SetupDashboardWidgetGenericFields className="max-w-120 flex-1" form={form} />

				<div className="relative flex-1">
					{/* position the preview absolutely so it follows the generic fields height */}
					<SetupDashboardWidgetPreview className="absolute inset-0" form={form} timeRange={timeRange} />
				</div>
			</div>

			<Tabs
				className="mt-4 flex flex-1 flex-col"
				onValueChange={setActiveTab}
				value={isMarkdownKind && activeTab === TAB_QUERIES ? TAB_SETTINGS : activeTab}
			>
				<TabsList
					className={cx("sticky -top-4 z-1 -mx-4 justify-start bg-gray-100 p-4 pt-2", isSticky && "shadow-md")}
					ref={tabsListRef}
				>
					{!isMarkdownKind && <TabsTrigger value={TAB_QUERIES}>Queries</TabsTrigger>}
					<TabsTrigger value={TAB_SETTINGS}>Settings</TabsTrigger>
					<TabsTrigger value={TAB_LINKS}>Links</TabsTrigger>
					<TabsTrigger value={TAB_YAML}>YAML</TabsTrigger>
				</TabsList>

				<TabsContent value={TAB_QUERIES}>
					<SetupDashboardWidgetQueries
						queries={form.fields.kind.branches.metrics.fields.queries}
						timeRange={timeRange}
					/>
				</TabsContent>

				<TabsContent className="h-full flex-1" value={TAB_SETTINGS}>
					{isMarkdownKind ? (
						<MarkdownSettings text={form.fields.kind.branches.markdown} />
					) : (
						<Settings settings={form.fields.kind.branches.metrics.fields.settings} />
					)}
				</TabsContent>

				<TabsContent value={TAB_LINKS}>
					<SetupDashboardWidgetLinks links={form.fields.links} />
				</TabsContent>

				<TabsContent value={TAB_YAML}>
					<SetupDashboardWidgetYaml form={form} />
				</TabsContent>
			</Tabs>
		</Form>
	);
};
